import type { SupportedLocale } from "~/i18n/types";
import { defaultLocale, supportedLocales } from "~/i18n/types";

/**
 * Parse Accept-Language header and return the best matching supported language
 */
export function detectLanguageFromHeader(acceptLanguageHeader?: string): SupportedLocale {
  if (!acceptLanguageHeader) {
    return defaultLocale;
  }

  // Parse Accept-Language header format: "en-US,en;q=0.9,es;q=0.8"
  const languages = acceptLanguageHeader
    .split(",")
    .map((lang) => {
      const parts = lang.trim().split(";");
      const code = parts[0].toLowerCase();
      const quality = parts[1] ? Number.parseFloat(parts[1].split("=")[1]) : 1.0;
      return { code, quality };
    })
    .sort((a, b) => b.quality - a.quality); // Sort by quality score

  // Find first supported language
  for (const { code } of languages) {
    // Try exact match first (e.g., "es")
    if (supportedLocales.includes(code as SupportedLocale)) {
      return code as SupportedLocale;
    }

    // Try language prefix (e.g., "en-US" -> "en")
    const langPrefix = code.split("-")[0];
    if (supportedLocales.includes(langPrefix as SupportedLocale)) {
      return langPrefix as SupportedLocale;
    }
  }

  return defaultLocale;
}

/**
 * Detect language on client-side using navigator.language
 */
export function detectClientLanguage(): SupportedLocale {
  if (typeof navigator === "undefined") {
    return defaultLocale;
  }

  const browserLang = navigator.language || navigator.languages?.[0];
  if (!browserLang) {
    return defaultLocale;
  }

  const langCode = browserLang.toLowerCase();

  // Try exact match first
  if (supportedLocales.includes(langCode as SupportedLocale)) {
    return langCode as SupportedLocale;
  }

  // Try language prefix
  const langPrefix = langCode.split("-")[0];
  if (supportedLocales.includes(langPrefix as SupportedLocale)) {
    return langPrefix as SupportedLocale;
  }

  return defaultLocale;
}
